import { TwoUIBiCustomClick, useTwoUITelemetry } from "@amx/ux-twoui-telemetry";
import { useTheme } from "@amx/ux-twoui-theming";
import { OmitStrict } from "@amx/ux-twoui-utils";
import { Breadcrumb as FluentBreadcrumb, IBreadcrumbItem as FluentBreadcrumbItem, Link, Stack, Text as FluentText } from "@fluentui/react";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useComponentLogger } from "../../shared/logging";
import { warnDeprecation } from "../../shared/warnDeprecation";
import { useViewport } from "../../styles";
import { getFontRampStyle, HeadingTypes } from "../../styles/fonts";
import { IconName } from "../../styles/icons";
import { Icon, IconProps } from "../Icon/Icon";
import { Image } from "../Image/Image";
import { AltImageProps } from "../Image/Image.types";
import { TooltipHost } from "../TooltipHost/TooltipHost";
import { TruncatedText } from "../TruncatedText/TruncatedText";
import {
    chevronLargeSize,
    crumbMargin,
    getBreadcrumbStyles,
    getCrumbIconStyles,
    getCrumbImageStyles,
    getCrumbLinkStyles,
    getCrumbStyles,
    getCrumbTextStyle,
    largeMediaMargin,
    mediaLargeSize,
    mediaSmallSize,
    smallMediaMargin
} from "./Breadcrumb.styles";

const defaultOverflowIndex = 0;
const defaultMaxDisplayedItems = 3;

/**
 * Type to specify what style should be used when rendering breadcrumb items.
 * Default uses the standard FluentUI Breadcrumb item rendering.
 * Title uses a custom render function that allows Breadcrumb items to contain graphics
 * and changes the style of the rest/hover/active states.
 */
export const BreadcrumbItemStyle = {
    Default: 0,
    Title: 1
};

export type BreadcrumbItemStyle = typeof BreadcrumbItemStyle[keyof typeof BreadcrumbItemStyle];

export interface BreadcrumbAltImageProps extends OmitStrict<AltImageProps, "height" | "width"> {
    readonly kind: "image";
}

export interface BreadcrumbIconProps extends OmitStrict<IconProps, "size"> {
    readonly kind: "icon";
}

interface BaseBreadcrumbItemProps {
    /**
     * Text to display to the user for the breadcrumb item.
     */
    readonly text: string;
    /**
     * Unique id associated with the breadcrumb item.
     */
    readonly id: string;
    /**
     * mediaProps allows you to include an optional image or icon beside the title
     * (for instance a logo). Only works well for square images/icons
     */
    readonly mediaProps?: BreadcrumbAltImageProps | BreadcrumbIconProps;
}

export interface BreadcrumbItemHyperlink extends BaseBreadcrumbItemProps {
    /**
     * Url to navigate to when this breadcrumb item is clicked.
     */
    readonly href: string;
    /**
     * Type of Breadcrumb item
     */
    readonly type: "hyperlink";
}

export interface BreadCrumbItemReactRoute extends BaseBreadcrumbItemProps {
    /**
     * React route for the link
     */
    // make sure to clean this up when we work on the vNext for Breadcrumb, scenario #31010214
    readonly to: string;
    /**
     * Type of Breadcrumb item
     */
    readonly type: "route";
}

export interface BreadCrumbItemText extends BaseBreadcrumbItemProps {
    /**
     * Type of Breadcrumb item
     */
    readonly type: "text";
}

export type BreadcrumbItem = BreadcrumbItemHyperlink | BreadCrumbItemReactRoute | BreadCrumbItemText;

export interface BreadcrumbProps {
    /**
     * Collection of breadcrumbs to render
     */
    readonly items: BreadcrumbItem[]; // tslint:disable-line: readonly-array
    /**
     * Optional index where overflow items will be collapsed. Defaults to 0.
     */
    readonly overflowIndex?: number;
    /**
     * The maximum number of breadcrumbs to display before coalescing. Defaults to 3.
     */
    readonly maxDisplayedItems?: number;
    /**
     * Page Area to be sent for BI.
     */
    readonly pageArea: string;
    /**
     * Prefix to be added to the item id when firing BI events.
     */
    readonly dataBiPrefix: string;
    /**
     * Aria label for the root element of the breadcrumb (which is a navigation landmark).
     */
    readonly ariaLabel: string;
    /**
     * Aria label for the overflow button.
     */
    readonly overflowAriaLabel: string;
    /**
     * Whether the last item in this breadcrumb is the current page displayed.
     * If true, `aria-current="page"` will be applied to the last breadcrumb item.
     */
    readonly isIncludingCurrentPage: boolean;
    /**
     * Optional style specification for how the items in the Breadcrumb should render.
     * Defaults to the "default" FluentUI Breadcrumb item rendering.
     */
    readonly itemStyle?: BreadcrumbItemStyle;
}

/** @deprecated External users, please use BannerBreadcrumbTitleColumn instead. Breadcrumb will be for internal users only. */
export const Breadcrumb: React.FC<BreadcrumbProps> = ({
    items,
    overflowIndex = defaultOverflowIndex,
    maxDisplayedItems = defaultMaxDisplayedItems,
    pageArea,
    dataBiPrefix,
    isIncludingCurrentPage,
    itemStyle = BreadcrumbItemStyle.Default,
    ...otherProps
}) => {
    const twoUITelemetry = useTwoUITelemetry();
    useComponentLogger("Breadcrumb");

    useEffect(() => {
        warnDeprecation("Breadcrumb has been deprecated, please use the new BannerBreadcrumbTitleColumn instead.");
    }, []);

    const bannerTitleTag = "h1";

    const viewportType = useViewport();

    const history = useHistory();

    const theme = useTheme();

    const isBreadcrumbTitle = itemStyle === BreadcrumbItemStyle.Title;

    // Collapse the text of the first crumb only if it contains a graphic
    const collapseFirstItem = items.length > 1 && items[0].mediaProps;

    // Values for crumb widths
    const divRef = useRef<HTMLDivElement>(null);
    const initialTitleWidth = 500;
    const [lastTitleWidthStyle, setLastTitleWidthStyle] = useState<number | undefined>(initialTitleWidth);
    const [firstTitleMaxWidthStyle, setFirstTitleMaxWidthStyle] = useState<number | undefined>(undefined);

    function getSpan(currentHeadingVariant: HeadingTypes) {
        const span = document.createElement("span");
        const fontRampStyles = getFontRampStyle(theme, currentHeadingVariant);
        switch (currentHeadingVariant) {
            case "paneTitle":
                span.style.fontFamily = theme.fonts.medium?.fontFamily ?? "Segoe UI";
                break;
            case "statusHeader":
                span.style.fontFamily = theme.fonts.large?.fontFamily ?? "Segoe UI";
                break;
        }

        span.style.fontSize = fontRampStyles.fontSize;
        span.style.fontWeight = fontRampStyles.fontWeight.toLocaleString();
        document.body.appendChild(span);
        return span;
    }

    function getTextWidth(str: string, currentHeadingVariant: HeadingTypes) {
        const span = getSpan(currentHeadingVariant);
        span.innerText = str;
        const roundedTextWidth = Math.ceil(span.getBoundingClientRect().width);

        span.remove();

        // Add right margin to title
        return roundedTextWidth + crumbMargin;
    }

    function getTitleWidth(str: string, displayText: boolean, hasMedia: boolean) {
        let currentHeadingVariant: HeadingTypes = "paneTitle";
        let currentMediaSize = mediaSmallSize;
        switch (viewportType) {
            case "l":
            case "xl":
            case "xxl":
            case "xxxl":
                currentHeadingVariant = "statusHeader";
                currentMediaSize = mediaLargeSize;
                break;
        }

        // If the first title has media and it is not the only title, the text collapses
        const textWidth = displayText ? getTextWidth(str, currentHeadingVariant) : 0;

        // If the title contains media, add the media pixels, otherwise add the left margin
        // tslint:disable-next-line: no-magic-numbers
        return (hasMedia ? textWidth + currentMediaSize + smallMediaMargin + largeMediaMargin : textWidth + crumbMargin);

    }

    /*
    * This function updates the width for the last title. If the first title is long, it sets a max width.
    */
    const updateWidth = () => {
        if (divRef.current) {
            const divBuffer = 0.85;
            const minLastTitleWidthProportion = 0.7;
            const totalWidth = (divRef.current.offsetWidth * divBuffer) - chevronLargeSize; // Remove pixels to account for chevron separator

            if (totalWidth > 0) {
                if (items.length === 1) {
                    setLastTitleWidthStyle(totalWidth);
                } else {
                    // Get widths of elements
                    const firstTitleWidth = getTitleWidth(items[0].text, !collapseFirstItem, items[0].mediaProps ? true : false);
                    const lastTitleWidth = getTitleWidth(items[items.length - 1].text, true, items[items.length - 1].mediaProps ? true : false);

                    // Calculate min/max width for the last title
                    const maxWidthLastTitle = Math.floor(totalWidth - firstTitleWidth);
                    let minLastTitleWidth = Math.floor(totalWidth * minLastTitleWidthProportion);
                    minLastTitleWidth = minLastTitleWidth > lastTitleWidth ? lastTitleWidth : minLastTitleWidth;

                    if (maxWidthLastTitle < minLastTitleWidth) {
                        // If the first title is long, reduce the width of the first title
                        setFirstTitleMaxWidthStyle(Math.floor(totalWidth - minLastTitleWidth));
                        setLastTitleWidthStyle(minLastTitleWidth);
                    } else {
                        const bufferedLastTitleWidth = lastTitleWidth > maxWidthLastTitle ? maxWidthLastTitle : lastTitleWidth;
                        setFirstTitleMaxWidthStyle(undefined);
                        setLastTitleWidthStyle(bufferedLastTitleWidth);
                    }
                }
            } else {
                setFirstTitleMaxWidthStyle(undefined);
                setLastTitleWidthStyle(undefined);
            }
        }
    };

    useEffect(() => {
        window.addEventListener("resize", updateWidth);
    }, []);

    const onItemClick = (_evt?: any, item?: FluentBreadcrumbItem) => {
        const click: TwoUIBiCustomClick = {
            clickId: `${dataBiPrefix}-${item?.key}`,
            pageArea: pageArea
        };
        twoUITelemetry.reportClick(click);
    };

    const mappedItems: FluentBreadcrumbItem[] = items?.map((item, index) => {
        switch (item.type) {
            case "route":
                return {
                    text: item.text,
                    key: item.id,
                    // make sure to clean this up when we work on the vNext for Breadcrumb, scenario #31010214
                    onClick: (_evt?: any, breadcrumbItem?: FluentBreadcrumbItem) => {
                        onItemClick(_evt, breadcrumbItem);
                        history.push(item.to);
                    },
                    isCurrentItem: isIncludingCurrentPage && index === items.length - 1
                };
            case "hyperlink":
                return {
                    text: item.text,
                    key: item.id,
                    href: item.href,
                    onClick: onItemClick,
                    isCurrentItem: isIncludingCurrentPage && index === items.length - 1
                };
            case "text":
                return {
                    text: item.text,
                    key: item.id,
                    isCurrentItem: isIncludingCurrentPage && index === items.length - 1
                };
        }
    });

    useEffect(() => {
        mappedItems.forEach((item) => {
            twoUITelemetry.checkUrlAndReportHttp(item.href);
        });
    }, [mappedItems]);

    // Custom sizes for renderCrumbs function
    const [headingVariant, setHeadingVariant] = useState<HeadingTypes>("paneTitle");
    const [mediaSize, setMediaSize] = useState<IconProps["size"]>(`${mediaSmallSize}px`);

    useEffect(() => {
        updateWidth();
        switch (viewportType) {
            case "s":
            case "m":
                setHeadingVariant("paneTitle");
                setMediaSize(`${mediaSmallSize}px`);
                break;
            case "l":
            case "xl":
            case "xxl":
            case "xxxl":
                setHeadingVariant("statusHeader");
                setMediaSize(`${mediaLargeSize}px`);
                break;
        }
    }, [viewportType]);

    /*
     * Custom render function for crumbs when Breadcrumb is used as a title in the Banner component.
     * The default FluentUI render function is used when Breadcrumb is not used as a title.
     */
    const renderCrumbs = (props?: FluentBreadcrumbItem): JSX.Element => {
        // Get current index information
        const lastItem = props?.key === items[items.length - 1].id;
        const firstItem = props?.key === items[0].id;
        const index = items.findIndex((breadcrumbItem) => breadcrumbItem.id === props?.key);

        // Get media element
        let mediaNode: JSX.Element | undefined;
        let crumbIconName: IconName | undefined;
        // Do not apply padding if the firstItem is not the only item or if there is no text
        const addExtraPadding = firstItem && !lastItem ? false : items[index].text === "" ? false : true;
        switch (items[index].mediaProps?.kind) {
            case "image":
                mediaNode = (
                    <Stack styles={getCrumbImageStyles(addExtraPadding)}>
                        <Image
                            imageType="altTextImage"
                            {...(items[index].mediaProps as AltImageProps)}
                            height={mediaSize}
                            width={mediaSize}
                        />
                    </Stack>
                );
                break;
            case "icon":
                crumbIconName = (items[index].mediaProps as IconProps).iconName;
                mediaNode = (
                    <Stack styles={getCrumbIconStyles(addExtraPadding)}>
                        <Icon
                            {...(items[index].mediaProps as IconProps)}
                            size={mediaSize}
                        />
                    </Stack>
                );
                break;
        }

        // Link props
        const linkProps = {
            href: props!.href,
            styles: getCrumbLinkStyles(theme, crumbIconName),
            "data-bi-id": props!.text,
            "aria-label": props!.text,
            onClick: props!.onClick,
            underline: false
        };

        // Return crumb rendering
        return (
            lastItem || items[index].type === "text" ?
                (
                    <Stack
                        horizontal={true}
                        styles={lastItem ? getCrumbStyles(mediaNode === undefined, undefined, lastTitleWidthStyle) :
                            getCrumbStyles(mediaNode === undefined, firstItem ? firstTitleMaxWidthStyle : undefined, undefined)}
                    >
                        {mediaNode}
                        <TruncatedText
                            type="heading"
                            tag={bannerTitleTag}
                            lines={1}
                            variant={headingVariant}
                            lineHeight={1.4}
                        >
                            {props!.text}
                        </TruncatedText>
                    </Stack>
                ) :
                (
                    (collapseFirstItem && props?.key === items[0].id) ?
                        (
                            <TooltipHost tooltipText={props!.text} tooltipPosition={"top-center"}>
                                <Link {...linkProps}>
                                    <Stack horizontal={true} >
                                        {mediaNode}
                                    </Stack>
                                </Link>
                            </TooltipHost>
                        ) :
                        (
                            <TooltipHost tooltipText={props!.text} tooltipPosition={"top-center"}>
                                <Link {...linkProps}>
                                    <Stack
                                        horizontal={true}
                                        styles={getCrumbStyles(mediaNode === undefined, firstItem ? firstTitleMaxWidthStyle : undefined, undefined)}
                                    >
                                        {mediaNode}
                                        <FluentText className="crumbTitle" styles={getCrumbTextStyle(theme, headingVariant)}>
                                            {props!.text}
                                        </FluentText>
                                    </Stack>
                                </Link>
                            </TooltipHost>
                        )
                )
        );
    };

    const fluentBreadcrumbComponent = (
        <FluentBreadcrumb
            styles={getBreadcrumbStyles(theme, isBreadcrumbTitle)}
            items={mappedItems}
            overflowIndex={overflowIndex}
            maxDisplayedItems={maxDisplayedItems}
            onRenderItem={isBreadcrumbTitle ? renderCrumbs : undefined}
            focusZoneProps={{ handleTabKey: 1 }}
            {...otherProps}
        />
    );

    return (
        isBreadcrumbTitle ?
            (
                <div className="divRef" ref={divRef}>
                    {fluentBreadcrumbComponent}
                </div>
            ) :
            fluentBreadcrumbComponent
    );
};
